import { SmoothGraphics as Graphics } from '@pixi/graphics-smooth';

import { CommonEdge } from 'components/Visualization/PixiGraph/common/Edge';
import {
  EdgeElements,
  END_CIRCLE_RADIUS,
} from 'components/Visualization/PixiGraph/common/EdgeElements';
import { AGraph } from 'components/Visualization/PixiGraph/core/Graph';
import { AGraphNode } from 'components/Visualization/PixiGraph/core/Node';
import { colors } from 'components/Visualization/PixiGraph/style';

import { AGraphNodeId } from './/types';
import { APoint } from './Point';
import { AGraphEdgeId } from './types';

const ACTIVITY_EDGE_HIT_PADDING = 5;
const ACTIVITY_EDGE_HIT_SEGMENTS = 16;

const toPixiColor = (color: number | string) =>
  typeof color === 'string'
    ? Number.parseInt(color.replace('#', ''), 16)
    : color;

const getBezierPoint = (t: number, start: APoint, end: APoint) => {
  const u = 1 - t;
  const controlPoint1 = {
    x: (end.x + start.x) / 2,
    y: start.y,
  };
  const controlPoint2 = {
    x: start.x + (end.x - start.x) / 3,
    y: end.y,
  };

  return {
    x:
      u * u * u * start.x +
      3 * u * u * t * controlPoint1.x +
      3 * u * t * t * controlPoint2.x +
      t * t * t * end.x,
    y:
      u * u * u * start.y +
      3 * u * u * t * controlPoint1.y +
      3 * u * t * t * controlPoint2.y +
      t * t * t * end.y,
  };
};

interface AGraphEdgeProps {
  source: AGraphNodeId;
  dest: AGraphNodeId;
}

export class AGraphEdge {
  id: AGraphEdgeId;
  source: AGraphNodeId;
  dest: AGraphNodeId;

  data: Record<string, any> = {};

  start: APoint = APoint.origin();
  end: APoint = APoint.origin();

  static getId(sourceId: AGraphNodeId, destId: AGraphNodeId): AGraphEdgeId {
    return `${sourceId}#${destId}`;
  }

  constructor(props: AGraphEdgeProps) {
    const { source, dest, ...rest } = props;
    // this.id = `${generateString(32)}`;
    this.id = AGraphEdge.getId(source, dest);
    this.source = source;
    this.dest = dest;
    this.data = rest;
  }

  render(g: Graphics): void {
    const { properties = {} } = this.data ?? {};

    const { db_user_name = '', encrypted } = properties;
    let color = 0xaaaaaa;

    if (db_user_name) {
      if (encrypted) {
        color = 0x00ff00;
      } else {
        color = 0xff0000;
      }
    }

    const edgeColor = this.data?.activityEdgeColor
      ? toPixiColor(this.data.activityEdgeColor)
      : color;
    const edgeWidth = this.data?.activityEdgeWidth ?? 2;

    CommonEdge.render(this, g, { color: edgeColor, width: edgeWidth });
    EdgeElements.renderEndCircle(this, g, { color: edgeColor });
    EdgeElements.renderStartCircle(this, g, { color: edgeColor });
    EdgeElements.renderEdgeIcon(this, g);
    EdgeElements.renderEdgeText(this, g);
  }

  renderActive(g: Graphics) {
    CommonEdge.render(this, g, {
      color: colors.primary,
      width: this.data?.activityEdgeWidth ?? 2,
    });
    EdgeElements.renderEndCircle(this, g, { color: colors.primary });
    EdgeElements.renderStartCircle(this, g, { color: colors.primary });
  }

  addInteractiveElement(graph: AGraph<AGraphNode, AGraphEdge>, index: number) {
    if (this.data.disabled) return;
    const { start, end } = this;
    // add the node hover bound
    graph.addInteractiveElement({
      minX: end.x - END_CIRCLE_RADIUS,
      maxX: end.x + END_CIRCLE_RADIUS,
      minY: end.y - END_CIRCLE_RADIUS,
      maxY: end.y + END_CIRCLE_RADIUS,
      minZ: index,
      maxZ: index,
      id: 'edge:end:' + this.id,
      data: this,
    });

    graph.addInteractiveElement({
      minX: start.x - END_CIRCLE_RADIUS,
      maxX: start.x + END_CIRCLE_RADIUS,
      minY: start.y - END_CIRCLE_RADIUS,
      maxY: start.y + END_CIRCLE_RADIUS,
      minZ: index,
      maxZ: index,
      id: 'edge:start:' + this.id,
      data: this,
    });

    graph.addInteractiveElement({
      ...EdgeElements.renderEdgeIconBoundary(this),
      minZ: index,
      maxZ: index,
      id: 'edge-info:' + this.id,
      data: this,
    });

    if (this.data?.activityEdgeLabel) {
      for (
        let segment = 0;
        segment < ACTIVITY_EDGE_HIT_SEGMENTS;
        segment += 1
      ) {
        const startPoint = getBezierPoint(
          segment / ACTIVITY_EDGE_HIT_SEGMENTS,
          start,
          end,
        );
        const endPoint = getBezierPoint(
          (segment + 1) / ACTIVITY_EDGE_HIT_SEGMENTS,
          start,
          end,
        );

        graph.addInteractiveElement({
          minX: Math.min(startPoint.x, endPoint.x) - ACTIVITY_EDGE_HIT_PADDING,
          maxX: Math.max(startPoint.x, endPoint.x) + ACTIVITY_EDGE_HIT_PADDING,
          minY: Math.min(startPoint.y, endPoint.y) - ACTIVITY_EDGE_HIT_PADDING,
          maxY: Math.max(startPoint.y, endPoint.y) + ACTIVITY_EDGE_HIT_PADDING,
          minZ: index,
          maxZ: index,
          id: `edge:path:${this.id}:${segment}`,
          data: this,
        });
      }
    }
  }

  renderHover(g: Graphics, id: string) {
    if (this.data?.activityEdgeLabel) {
      CommonEdge.renderHover(this, g, {
        width: this.data?.activityEdgeWidth ?? 2,
      });
      EdgeElements.renderEndCircle(this, g, { color: colors.primary });
      EdgeElements.renderStartCircle(this, g, { color: colors.primary });
      return;
    }
    if (id.includes('edge:end:')) {
      EdgeElements.renderEndCircle(this, g, { color: colors.primary });
    }
    if (id.includes('edge:start:')) {
      EdgeElements.renderStartCircle(this, g, { color: colors.primary });
    }
  }
}
